import DOMPurify from "dompurify";
import {
  Linkedin, Share2, Mail, Phone, MessageCircle, Sparkles, X, Copy, Check, Download,
} from "lucide-react";

export const CHANNEL_ORDER = [
  ["linkedin",          Linkedin],
  ["twitter",           Share2],
  ["twitter_thread",    Share2],
  ["email_footer_html", Mail],
  ["email_pspacs",      Mail],
  ["cold_email",        Mail],
  ["sms",               Phone],
  ["whatsapp",          MessageCircle],
  ["instagram",         Sparkles],
];

/**
 * Share-kit modal. Split from AIVisibilityHub.jsx.
 * Preserves every data-testid and rendering behavior of the original.
 */
export default function ShareKitModal({ kit, tab, setTab, copiedChan, onCopy, onClose }) {
  const active = kit.channels?.[tab] || {};
  return (
    <div
      data-testid="share-kit-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl border border-neutral-900 shadow-2xl my-8"
      >
        <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300">Audit share kit</div>
            <div className="text-xl font-bold mt-1">Promote /audit in 10 seconds</div>
            <div className="text-xs text-neutral-400 mt-0.5 font-mono truncate max-w-lg">{kit.audit_url}</div>
          </div>
          <button onClick={onClose} data-testid="share-kit-close" className="text-neutral-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
          {/* Channel picker */}
          <div className="border-r border-neutral-200 bg-neutral-50">
            {CHANNEL_ORDER.map(([key, Icon]) => {
              const ch = kit.channels?.[key];
              if (!ch) return null;
              const isActive = tab === key;
              return (
                <button
                  key={key}
                  data-testid={`share-kit-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`w-full text-left px-4 py-3 border-b border-neutral-200 flex items-center gap-2 text-sm ${isActive ? "bg-white text-neutral-900 font-semibold border-l-2 border-l-coral" : "text-neutral-600 hover:bg-white"}`}
                >
                  <Icon className="w-4 h-4" />
                  {ch.label}
                </button>
              );
            })}
            <div className="p-4 border-b border-neutral-200">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">QR code</div>
              {kit.qr_url ? (
                <>
                  <img src={kit.qr_url} alt="/audit QR" className="w-full border border-neutral-200 bg-white" data-testid="share-kit-qr-img" />
                  <a
                    href={kit.qr_url}
                    download="audit-qr.png"
                    target="_blank" rel="noopener noreferrer"
                    className="lf-btn-outline text-xs px-3 py-2 mt-2 inline-flex items-center gap-1 w-full justify-center"
                    data-testid="share-kit-qr-download"
                  >
                    <Download className="w-3 h-3" /> Download PNG
                  </a>
                </>
              ) : (
                <div className="text-xs text-neutral-400">Set APP_URL to enable QR.</div>
              )}
            </div>
          </div>

          {/* Preview + copy */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Preview</div>
                <div className="text-lg font-bold text-neutral-900 mt-0.5">{active.label}</div>
              </div>
              <button
                onClick={() => onCopy(tab, active.content || "")}
                className="lf-btn-dark text-xs px-3 py-2 inline-flex items-center gap-1"
                data-testid={`share-kit-copy-${tab}`}
              >
                {copiedChan === tab ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            {active.format === "html" ? (
              <>
                <div className="border border-neutral-200 p-4 bg-neutral-50 mb-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Rendered preview</div>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(active.content, { USE_PROFILES: { html: true } }) }} />
                </div>
                <details>
                  <summary className="text-xs font-mono uppercase tracking-widest text-neutral-500 cursor-pointer">Raw HTML</summary>
                  <pre className="whitespace-pre-wrap text-xs bg-neutral-900 text-neutral-100 p-4 mt-2 max-h-72 overflow-auto">{active.content}</pre>
                </details>
              </>
            ) : (
              <pre className="whitespace-pre-wrap text-sm bg-neutral-50 border border-neutral-200 p-4 max-h-96 overflow-auto font-sans text-neutral-800">{active.content}</pre>
            )}
            <div className="mt-4 text-xs text-neutral-500">
              Tip: Personalise the <code>{"{contact_name}"}</code>, <code>{"{business_name}"}</code>, <code>{"{industry}"}</code> and <code>{"{sender_name}"}</code> placeholders before sending. All URLs already point to your live /audit page.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
